import { ConversationApi, FlowApi, MessagesApi } from 'api'
import { AxiosPromise } from 'axios'
import { triggerConversationStartedAPIEvent } from 'modules/api/events'
import { getCampaignAttributeById } from 'modules/embed/selectors'
import { getCampaignIdFromInteractionId, getCampaignPropertyById } from 'modules/embed/utils'
import { createMessage, createPreMessagesForCampaign } from 'modules/message/creators'
import { AgentAvailabilityStatusUpdate, ConversationId } from 'types/conversation'
import { Message } from 'types/message'

interface GetAllConversationsArgs {
  limit?: number,
  endUserId: string
}

interface NewConversationWithMeetingOptions {
  campaignMessage?: Message
}

export const getConversationById = (id: ConversationId) => {
  return ConversationApi.get(`/conversations/end_users/widget/conversation_context/${id}`)
}

export const getAllConversations = ({ limit = 10, endUserId }: GetAllConversationsArgs) => {
  return ConversationApi.get(`conversations/end_users/${endUserId}/extra`, { params: { limit } })
}

export const bulkSendMessageReadReceipts = (conversationId: ConversationId, messageIds: number[]) => {
  return MessagesApi.post(`/messages/read-bulk`, { messageIds, conversationId })
}

export const sendConversationEventMessage = (eventPayload: { conversationId?: ConversationId }) => {
  return MessagesApi.post(`/conversations/${eventPayload.conversationId}/messages`,{
    type: 'CONVERSATION_EVENT',
    conversationEvent: eventPayload
  })
}

export const createConversationWithEventMessage = (eventPayload, attributes = {}) => {
  return MessagesApi.post(`/messages`,{
    attributes,
    body: "",
    status: 'BULK_SENT',
    type: 'CONVERSATION_EVENT',
    conversationEvent: eventPayload
  })
}

export const createBotConversation = async (interactionId: number, attributes: object = {}, hash?: string) => {
  const campaignIdForInteraction = getCampaignIdFromInteractionId(interactionId)
  const inboxIdForCampaign = getCampaignPropertyById(campaignIdForInteraction, 'inboxId')
  const playbookIdForCampaign = getCampaignAttributeById(campaignIdForInteraction, 'playbookId')

  const messagePayload: Record<string, any> = {
    attributes: {
      withGreeting: true,
      startInteraction: interactionId,
      interactionId,
      playbookId: playbookIdForCampaign,
      ...attributes,
    },
    status: 'BULK_SENT',
    isEndUserMessage: false
  }

  if (inboxIdForCampaign) {
    messagePayload.inboxId = inboxIdForCampaign
  }

  const message = createMessage(messagePayload)
  const messageResponse = await FlowApi.post<Message>(`/driftlink/messages`, message, {params: {hash: hash ? hash.replace('#', '') : undefined}})
  triggerConversationStartedAPIEvent(messageResponse.data)
  return messageResponse
}

export const createNewConversation = (message: string, attributes: object = {}) => {
  return MessagesApi.post(`/messages`, {
    body: message,
    attributes: {
      ...attributes,
      widgetVersion: 2
    }
  })
}

export const fetchAgentStatus = (agentId: number) : AxiosPromise<AgentAvailabilityStatusUpdate>  => {
  return ConversationApi.get(`/agents/${agentId}/status`)
}

export const createNewConversationWithMeeting = (endUserEmail: string, scheduleMeetingWith: number, locale: string, options?: NewConversationWithMeetingOptions) => {
  const createMessageArgs: Record<string, any> = {
    body: '',
    attributes: {
      scheduleMeetingFlow: true,
      scheduleMeetingWith,
      widgetApiTriggered: true,
      isTriggeredFromAPI: true,
      widgetVersion: 2,
      locale
    },
    status: 'BULK_SENT'
  }

  if (endUserEmail) {
    createMessageArgs.attributes.endUserEmail = endUserEmail
  }

  if (options && options.campaignMessage) {
    const { campaignMessage } = options
    createMessageArgs.attributes.relatedCampaignId = campaignMessage.attributes.campaignId
    createMessageArgs.preMessages = createPreMessagesForCampaign(campaignMessage)
  }

  const message = createMessage(createMessageArgs)

  return MessagesApi.post(`/messages`, message)
}

export const fetchConversationPermaclosedConfig = (conversationId: number) =>
  ConversationApi.get(`/conversations/${conversationId}/permaclosed`)
